const { areTimezonesCurrentlyEqual } = require('@/functions/date');
const { MODAL_NAMES } = require('@/vue/modals/MODAL_NAMES');

class TimeSyncer {
    /**
     * @param {ConnectivityChecker} connectivityChecker
     * @param {VueModalFactory} vueModalFactory
     */
    constructor(
        connectivityChecker,
        vueModalFactory
    ) {
        this.connectivityChecker = connectivityChecker;
        this.vueModalFactory = vueModalFactory;

        this.allowedMsDifference = 180000; // 3 minutes (in ms)

        /**
         * If the "time is out of sync" modal is currently shown, this will be the ID of the modal.
         *
         * @type {null}
         */
        this.timeErrorModalID = null;
        this.timezoneErrorModalID = null;

        /**
         * This flag says if the user has clicked the 'Recheck' button in the time error modal.
         * If they have, we will display some additional text.
         * This will never get reset to false until the application is reloaded. But that is fine.
         *
         * @type {boolean}
         */
        this.hasRechecked = false;

        this.hasTimeError = false;
        this.hasTimezoneError = false;
    }

    /**
     * Handle a response from the /status API endpoint, which contains the correct current time.
     *
     * @param {string} currentTime Current Time in ISO-8601 format.
     */
    handleTimeUpdate(currentTime) {
        const serverTime = new Date(currentTime);
        const systemTime = new Date();

        if (!this.isTimeDifferenceAcceptable(serverTime, systemTime)) {
            this.showTimeErrorDialog(serverTime, systemTime);
        } else {
            this.hideTimeErrorDialog();
        }
    }

    /**
     * @param {Date} serverTime
     * @param {Date} systemTime
     *
     * @returns {boolean}
     */
    isTimeDifferenceAcceptable(serverTime, systemTime) {
        const diff = Math.abs(serverTime.getTime() - systemTime.getTime());

        if (diff > this.allowedMsDifference) {
            console.error('Time difference is too big.', serverTime, systemTime, diff);
            return false;
        }

        return true;
    }

    showTimeErrorDialog(serverTime, systemTime) {
        // Close any existing dialog.
        this.hideTimeErrorDialog();

        this.vueModalFactory.open(
            MODAL_NAMES.TIME_ERROR,
            {
                timeError: true,
                serverTime,
                systemTime,
                hasRechecked: this.hasRechecked,
            },
            {
                onOpen: (modalID) => {
                    this.timeErrorModalID = modalID;
                },
            },
        );
    }

    hideTimeErrorDialog() {
        if (this.timeErrorModalID) {
            this.vueModalFactory.close(this.timeErrorModalID);
            this.timeErrorModalID = null;
        }
    }

    showTimezoneErrorDialog(branchTimezoneName, systemTimezoneName) {
        this.vueModalFactory.open(
            MODAL_NAMES.TIMEZONE_ERROR,
            {
                timezoneError: true,
                branchTimezoneName,
                systemTimezoneName,
                hasRechecked: this.hasRechecked,
            },
            {
                onOpen: (modalID) => {
                    this.timezoneErrorModalID = modalID;
                },
            },
        );
    }

    recheck() {
        this.hasRechecked = true;
        this.connectivityChecker.check();
    }

    getSystemTimezoneName() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            console.error('Failed to find the system timezone name.', e);
        }

        return null;
    }

    /**
     * The given branch should have a 'timezoneName'
     *
     * @param {Branch} branch
     */
    ensureComputerHasCorrectTimezoneForBranch(branch) {
        try {
            if (branch.timezoneName) {
                const systemTimezoneName = this.getSystemTimezoneName();

                if (systemTimezoneName.toLowerCase() !== branch.timezoneName.toLowerCase()) {
                    // The timezones names differ, but only show the modal if it matters!
                    // i.e. Europe/Berlin and Europe/Amsterdam are different names, but it doesn't matter because
                    // they're the same offset. However, if DST or some other reason causes these timezone names
                    // to become unequal it will show the error.
                    if (!areTimezonesCurrentlyEqual(branch.timezoneName, systemTimezoneName)) {
                        this.showTimezoneErrorDialog(branch.timezoneName, systemTimezoneName);
                        // This ensureComputerHasCorrectTimezoneForBranch function is called once during the ProPoint
                        // startup sequence. So if the error modal gets dismissed it will not reopen itself like the
                        // time error modal would (that one is more important). This means you can dismiss it and
                        // continue to use ProPoint with the wrong timezone if you know the secret to dismiss
                        // the modal.
                    }
                }
            }
        } catch (e) {
            // Handle cases where Intl.DateTimeFormat().resolvedOptions() is not available.
            console.error('Failed to compare the branch timezone to the system timezone.', e);
        }
    }
}

module.exports = TimeSyncer;
